import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, ShieldAlert, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { fmtTimeAgo } from '@/lib/realtime';

export default function AuditPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/audit/logs', { params: { action: filter || undefined, limit: 500 } });
      setLogs(data || []);
    } catch (e) { toast.error('Failed'); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold"><ShieldAlert className="mr-2 inline h-5 w-5 text-primary" /> Audit log</h1>
          <p className="text-xs text-muted-foreground">Sensitive actions are recorded for compliance</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="space-y-0"><Input data-testid="audit-filter-input" placeholder="Filter by action…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 w-56" /></div>
          <Button data-testid="audit-refresh-button" size="sm" variant="secondary" onClick={load}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        <Card>
          <CardHeader><CardTitle><FileText className="mr-1 inline h-4 w-4" /> Recent events ({logs.length})</CardTitle></CardHeader>
          <CardContent>
            {!logs.length && <p className="py-6 text-center text-sm text-muted-foreground">No audit events yet.</p>}
            <ul className="divide-y divide-border">
              {logs.map((l) => (
                <li key={l.id} data-testid="audit-row" className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm"><Badge className="mr-2">{l.action}</Badge>{l.actor_email && <span className="text-muted-foreground"> by {l.actor_email}</span>}</p>
                    {l.target && <p className="text-xs text-muted-foreground">target: <span className="font-mono">{l.target}</span></p>}
                    {l.meta && <p className="text-xs text-muted-foreground">meta: <span className="font-mono">{JSON.stringify(l.meta)}</span></p>}
                  </div>
                  <span className="text-xs text-muted-foreground tabular">{fmtTimeAgo(l.created_at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
